import { Injectable } from '@angular/core';
import { from, Observable } from 'rxjs';
import { getPublicUrl, supabase } from '../../core/supabase/supabase.client';

export interface Brand {
  id: number;
  name: string;
  slug: string;
  description?: string | null;
  icon_file_id?: number | null;
  brand_type?: 'national' | 'regional' | 'local' | 'private_label';
  status: 'draft' | 'published' | 'archived' | string; // Allow string for legacy compatibility
  is_featured?: boolean;
  
  // Legacy/Compatibility fields
  shortDescription?: string | null;
  primaryCategoryId?: number | null;
  secondaryCategoryIds?: number[] | null;
  featured?: boolean;
  priority?: number;
  accentColor?: string;
  metaTitle?: string | null;
  metaDescription?: string | null;
  logo?: string | null;
  keywords?: string[] | null;

  // Joins/Extra
  icon?: {
    id: number;
    path: string;
    bucket: string;
    url?: string;
  } | null;
  product_count?: number;

  createdAt?: string;
  updatedAt?: string;
}

export type CreateBrandPayload = Omit<Brand, 'id' | 'icon' | 'product_count' | 'updatedAt'>;

@Injectable({ providedIn: 'root' })
export class BrandService {
  private readonly table = 'brands';

  getBrandById(id: number): Observable<Brand> {
    return from(
      supabase
        .from(this.table)
        .select(`
          *,
          icon:files!brands_icon_file_id_fkey(id, path, bucket),
          product_count:products(count)
        `)
        .eq('id', id)
        .single()
        .then(({ data, error }) => {
          if (error) throw error;
          return this.mapBrand(data);
        })
    );
  }

  listBrands(filters?: {
    search?: string;
    type?: string;
    status?: string;
    featured?: boolean;
    sortBy?: string;
    order?: 'ASC' | 'DESC';
    limit?: number;
  }): Observable<Brand[]> {
    let query = supabase
      .from(this.table)
      .select(`
        *,
        icon:files!brands_icon_file_id_fkey(id, path, bucket),
        product_count:products(count)
      `);

    if (filters?.search) {
      query = query.ilike('name', `%${filters.search.trim()}%`);
    }
    if (filters?.type) {
      query = query.eq('brand_type', filters.type);
    }
    const status = filters?.status ?? 'published';
    if (status) {
      query = query.eq('status', status);
    }
    if (filters?.featured !== undefined) {
      query = query.eq('is_featured', filters.featured);
    }

    const sortBy = filters?.sortBy || 'created_at';
    const order = filters?.order || 'DESC';
    if (filters?.limit) {
      query = query.limit(filters.limit);
    }

    query = query.order(sortBy, { ascending: order === 'ASC' });

    return from(
      query.then(({ data, error }) => {
        if (error) throw error;
        return (data ?? []).map((row: any) => this.mapBrand(row));
      })
    );
  }

  searchBrands(query: string): Observable<Brand[]> {
    return this.listBrands({ search: query });
  }

  createBrand(payload: CreateBrandPayload): Observable<Brand> {
    return from(
      supabase
        .from(this.table)
        .insert({
          ...payload,
          updated_at: new Date().toISOString(),
        })
        .select('*')
        .single()
        .then(({ data, error }) => {
          if (error) throw error;
          return this.mapBrand(data);
        })
    );
  }

  updateBrand(
    id: number,
    patch: Partial<CreateBrandPayload>
  ): Observable<Brand> {
    return from(
      supabase
        .from(this.table)
        .update({ ...patch, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select('*')
        .single()
        .then(({ data, error }) => {
          if (error) throw error;
          return this.mapBrand(data);
        })
    );
  }

  updateStatus(id: number, status: Brand['status']): Observable<void> {
    return from(
      supabase
        .from(this.table)
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', id)
        .then(({ error }) => {
          if (error) throw error;
        })
    );
  }

  deleteBrand(id: number): Observable<void> {
    return from(
      supabase
        .from(this.table)
        .delete()
        .eq('id', id)
        .then(({ error }) => {
          if (error) throw error;
        })
    );
  }

  private mapBrand(row: any): Brand {
    const icon = row.icon ? {
      ...row.icon,
      url: getPublicUrl(row.icon.bucket, row.icon.path)
    } : null;

    return {
      id: row.id,
      name: row.name,
      slug: row.slug,
      description: row.description,
      icon_file_id: row.icon_file_id,
      brand_type: row.brand_type || 'national',
      status: row.status || 'draft',
      is_featured: !!row.is_featured,
      
      // Legacy mapping
      shortDescription: row.shortDescription ?? row.short_description ?? row.description,
      primaryCategoryId: row.primaryCategoryId ?? row.primary_category_id,
      secondaryCategoryIds: row.secondaryCategoryIds ?? row.secondary_category_ids,
      featured: !!(row.featured ?? row.is_featured),
      priority: row.priority,
      accentColor: row.accentColor ?? row.accent_color,
      metaTitle: row.metaTitle ?? row.meta_title,
      metaDescription: row.metaDescription ?? row.meta_description,
      logo: row.logo ?? icon?.url,
      keywords: row.keywords,

      icon,
      product_count: row.product_count?.[0]?.count ?? 0,
      createdAt: row.created_at || row.createdAt,
      updatedAt: row.updated_at || row.updatedAt,
    };
  }

}
